<template>
  <v-data-table
    :headers="headers"
    :items="filterActivities"
    :page.sync="pagination.page"
    :items-per-page.sync="paginationActivitiesTable.rowsPerPage"
    :loading="loading"
    loading-text=""
    :items-per-page-text="rowsPerPageText"
    :items-per-page-items="rowsPerPageItems"
    :header-props="{ sortIcon }"
    :footer-props="{ prevIcon, nextIcon, 'items-per-page-options':rowsPerPageItems  }"
    @pagination="setRowsOnLocalStorage(dataTableActivitiesId,paginationActivitiesTable.rowsPerPage)"
    :class="{ 'no-show-permission': !userHasPermissionTo('show') }"
    class="elevation-2"
    style="margin: 20px"
  >
    <template v-slot:headers>
      <tr>
        <activity-table-header-cell
          :header="headers[0]"
          @sort="changeSort"
          :pagination="pagination"
          :filterActive="filters.state ? true : false"
        >
          <v-autocomplete
            v-model="filters.state"
            :label="$t('State')"
            :items="stateOptions"
            :pagination="pagination"
            clearable
          />
        </activity-table-header-cell>

        <activity-table-header-cell
          :header="headers[1]"
          @sort="changeSort"
          :pagination="pagination"
        />

        <activity-table-header-cell
          :header="headers[2]"
          @sort="changeSort"
          :pagination="pagination"
          :filterActive="filters.activity ? true : false"
        >
          <v-autocomplete
            v-model="filters.activity"
            :label="$tc('Activity', 1)"
            :items="activities"
            item-text="title"
            clearable
          />
        </activity-table-header-cell>

        <activity-table-header-cell
          :header="headers[3]"
          @sort="changeSort"
          :pagination="pagination"
          :filterActive="filters.layer ? true : false"
        >
          <v-autocomplete
            v-model="filters.layer"
            :label="$tc('Layer', 1)"
            :items="objectTypeOptions"
            item-value="layer"
            item-text="type_name"
            clearable
          />
        </activity-table-header-cell>

        <activity-table-header-cell
          :header="headers[4]"
          @sort="changeSort"
          :pagination="pagination"
          :filterActive="filters.labelObject ? true : false"
        >
          <v-autocomplete
            v-model="filters.labelObject"
            :label="$tc('Object', 1)"
            :items="activities"
            :item-text="
              item => (item.labelObject ? item.labelObject : 'No Objects')
            "
            :item-value="
              item => (item.labelObject ? item.labelObject : 'No Objects')
            "
            clearable
          />
        </activity-table-header-cell>

        <activity-table-header-cell
          :header="headers[5]"
          @sort="changeSort"
          :pagination="pagination"
          :filterActive="filters.creator ? true : false"
        >
          <v-autocomplete
            v-model="filters.creator"
            :label="$t('Creator')"
            :items="allUsers"
            :item-text="item => getUserName(item.id)"
            :item-value="item => item.id"
            clearable
          />
        </activity-table-header-cell>

        <activity-table-header-cell
          :header="headers[6]"
          @sort="changeSort"
          :pagination="pagination"
          :filterActive="filters.startDate ? true : false"
        >
          <v-menu
            v-model="startDateMenu"
            :close-on-content-click="false"
            :nudge-right="40"
            lazy
            transition="scale-transition"
            offset-y
            full-width
            min-width="290px"
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                v-model="filters.startDate"
                :label="$t('From')"
                append-icon="event"
                readonly
                clearable
                v-on="on"
              />
            </template>
            <v-date-picker
              v-model="filters.startDate"
              @input="startDateMenu = false"
              color="accent"
              no-title
            />
          </v-menu>

          <v-menu
            v-model="endDateMenu"
            :close-on-content-click="false"
            :nudge-right="40"
            lazy
            transition="scale-transition"
            offset-y
            full-width
            min-width="290px"
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                v-model="filters.endDate"
                :label="$t('To')"
                append-icon="event"
                readonly
                clearable
                v-on="on"
              />
            </template>
            <v-date-picker
              v-model="filters.endDate"
              @input="endDateMenu = false"
              color="accent"
              no-title
            />
          </v-menu>
        </activity-table-header-cell>
        <activity-table-header-cell
          :header="headers[7]"
          @sort="changeSort"
          :pagination="pagination"
        />
      </tr>
    </template>

    <template v-slot:item="props">
      <tr
        @click="$emit('openActivity', props.item.uuid)"
        :class="{ 'cursor-pointer': userHasPermissionTo('show') }"
      >
        <td>
          <activity-task-status-icon
            :status="props.item.state"
            :delayed="isDelayed(props.item)"
          />
        </td>

        <td>
          -
        </td>

        <td>
          {{ props.item.title }}
        </td>

        <td>
          {{ getObjectTypeLabel(props.item.object_type) }}
        </td>

        <td>
          {{ props.item.labelObject ? props.item.labelObject : "-" }}
        </td>

        <td>
          <v-layout row align-center>
            <v-icon
              v-if="getCreatorType(props.item.user_creator_id) !== '-'"
              small
              color="accent"
              class="mr-1"
            >
              {{ getCreatorType(props.item.user_creator_id) }}
            </v-icon>
            <span>{{ getUserName(props.item.user_creator_id) }}</span>
          </v-layout>
        </td>

        <td>
          {{ getFormattedDate(props.item.due_date) }}
        </td>
        <td>
          <v-btn
            icon
            text
            color="accent"
            @click="viewInMap(props.item.geoJson)"
          >
            <v-icon>gps_fixed</v-icon>
          </v-btn>
        </td>
      </tr>
    </template>

    <template v-slot:pageText="props">
      {{ props.pageStart }} - {{ props.pageStop }} {{ $t("tables.Of") }}
      {{ props.itemsLength }}
    </template>

    <template v-slot:no-data>
      <div v-if="loading" />
    </template>
  </v-data-table>
</template>

<script>
import { managementDatatableMixin } from "@/mixins/management/managementDatatableMixin";
import ActivityTaskStatusIcon from "@/components/FieldManagement/StatusIcon";
import ActivityTableHeaderCell from "@/components/FieldManagement/FieldWork/Data/ActivityListTableHeaderCell";
import DataTableNoResults from "@/components/_Atoms/DataTableNoResults";
import { mapActions } from "vuex";
import { activitiesTasksDataMixin } from "@/mixins/field/activitiesTasksDataMixin";

export default {
  name: "ActivitiesNetworkTable",

  props: ["workspaceUUID"],

  components: {
    DataTableNoResults,
    ActivityTableHeaderCell,
    ActivityTaskStatusIcon
  },

  mixins: [managementDatatableMixin, activitiesTasksDataMixin],

  data() {
    return {
      activities: [],
      filters: {
        state: undefined,
        layer: undefined,
        labelObject: undefined,
        activity: undefined,
        creator: undefined,
        startDate: undefined,
        endDate: undefined
      },
      dataTableActivitiesId: "dataTableActivitiesId" ,
      paginationActivitiesTable: {
        rowsPerPage: this.getRowsFromLocalStorage("dataTableActivitiesId")
      },
      startDateMenu: false,
      endDateMenu: false,

      permissionName: "field_activity"
    };
  },

  computed: {
    headers() {
      return [
        {
          text: this.$i18n.t("State"),
          value: "state",
          width: "5%",
          hasFilter: true
        },
        {
          text: this.$i18n.tc("Workspace", 2),
          value: "workspace",
          width: "5%",
          hasFilter: false
        },
        {
          text: this.$i18n.tc("Activity", 1),
          value: "title",
          width: "20%",
          hasFilter: true
        },
        {
          text: this.$i18n.tc("Layer", 1),
          value: "object_type",
          width: "5%",
          hasFilter: true
        },

        {
          text: this.$i18n.tc("Object", 1),
          value: "labelObject",
          width: "10%",
          hasFilter: true
        },

        {
          text: this.$i18n.t("Creator"),
          value: "user_creator_id",
          width: "5%",
          hasFilter: true
        },
        {
          text: this.$i18n.t("DueDate"),
          value: "due_date",
          width: "5%",
          hasFilter: true
        },
        {
          text: this.$i18n.t("Location"),
          value: "location",
          width: "5%",
          hasFilter: false
        }
      ];
    },
    filterActivities() {
      let conditions = [];
      if (this.filters.state) {
        conditions.push(this.filterActivityState);
      }
      if (this.filters.activity) {
        conditions.push(this.filterActivityTitle);
      }
      if (this.filters.layer) {
        conditions.push(this.filterActivityLayer);
      }

      if (this.filters.labelObject) {
        conditions.push(this.filterActivityObject);
      }
      if (this.filters.creator) {
        conditions.push(this.filterActivityCreator);
      }
      if (this.filters.startDate && this.filters.endDate) {
        conditions.push(this.filterActivityDate);
      }

      if (conditions.length > 0) {
        return this.activities.filter(workspace => {
          return conditions.every(condition => {
            return condition(workspace);
          });
        });
      }

      return this.activities;
    }
  },

  mounted() {
    this.loadActivities();
  },
  methods: {
    ...mapActions(["fetchMyActivities"]),

    async loadActivities() {
      let activities = [];
      try {
        this.loading = true;
        const response = await this.fetchMyActivities({
          filters: null
        });
        activities = response.activities;
        for (const activity of activities) {
          let label = activity.label;
          let geoJson = activity.geo_json;

          for (const activityObject of activity.activities) {
            activityObject.labelObject = label;
            activityObject.geoJson = geoJson;
            this.activities.push(activityObject);
          }
        }
      } catch (e) {
        console.log(e);
      } finally {
        this.loading = false;
      }
    },

    filterActivityState(item) {
      return item.state === this.filters.state;
    },
    filterActivityLayer(item) {
      return item.object_type === this.filters.layer;
    },
    filterActivityTitle(item) {
      return item.title === this.filters.activity;
    },
    filterActivityObject(item) {
      if (this.filters.labelObject === "No Objects") {
        return item.labelObject === null || item.labelObject === "";
      } else {
        return item.labelObject === this.filters.labelObject;
      }
    },
    filterActivityCreator(item) {
      return item.user_creator_id === this.filters.creator;
    },
    filterActivityDate(item) {
      return (
        item.due_date > this.filters.startDate &&
        item.due_date < this.filters.endDate
      );
    },

    changeSort(column) {
      if (this.pagination.sortBy === column) {
        if (!this.pagination.descending) {
          this.pagination.descending = true;
        } else {
          this.pagination.sortBy = null;
          this.pagination.descending = null;
        }
      } else {
        this.pagination.sortBy = column;
        this.pagination.descending = false;
      }
    },

    viewInMap(item) {
      this.$store.dispatch("SET_OBJECT_TO_ZOOM_ON_MAP", undefined).then(() => {
        this.$store.dispatch("SET_OBJECT_TO_ZOOM_ON_MAP", item).then(() => {
          let layoutMode = this.$store.getters.getActiveLayoutMode;
          if (layoutMode === 1) {
            this.$store.dispatch("SET_ACTIVE_FIBERCLOUD_TAB", "home");
          }
        });
      });
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/Home/Content/Tabs/Activities/ActivitiesTable.vue